import Link from '@material-ui/core/Link';
import clsx from 'clsx';
import {useEffect, useState} from 'react';
import * as React from 'react';
import {useDispatch} from 'react-redux';
import {FormApi, FormState} from 'final-form';
import {useSnackbar} from 'notistack';
import {FormSpy, Field, FormSpyRenderProps} from 'react-final-form';
import {Box, FormHelperText, makeStyles} from '@material-ui/core';

import {logEvent} from '~/modules/App/store/gaActions';
import {easySignUpForm} from '~/modules/FinalForm/constants/forms';
import {phoneKey, phoneCodeKey} from '~/modules/Auth/SignUp/formFieldsKeys';
import validateForm from '~/modules/Auth/SignUp/validateForm';
import AuthService from '~/modules/Auth/AuthService';
import errorSnackbar from '~/helpers/snackbar/errorSnackbar';
import routeByName from '~/constants/routes';
import useModalById from '~/customHooks/useModalById';
import {modalHideAction, modalShowAction} from '~/rootStore/modals/modalsActions';
import {MODAL_REGISTER_ANONYMOUSLY, MODAL_VERIFICATION_CODE} from '~/rootStore/modals/modalsIds';

import ModalBase from '../ModalBase';
import FinalFormReduxForm from '../finalFormWrappers/FinalFormReduxForm';
import FinalFormButton from '../finalFormWrappers/FinalFormButton';
import FinalFormPhoneInput from '../finalFormWrappers/inputs/FinalFormPhoneInput';
import {concatPhone} from '../ui/inputs/phoneHelpers/phoneHelpers';
import {defaultPhoneCode} from '../ui/inputs/PhoneInput/constants';
import ModalBaseContent from '../ModalBaseContent';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    margin: '10px 0px',
  },
  buttonContainer: {
    width: '100%',
  },
  proceedLink: {
    fontWeight: 'bold',
    marginLeft: 5,
    textDecoration: 'underline',
  },
}));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const JSFinalFormReduxForm: any = FinalFormReduxForm;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const JSFinalFormPhoneInput: any = FinalFormPhoneInput;

const defaultPhoneTextHelper: {text: string; phone: string | null} = {
  text: 'Phone number will be verified',
  phone: null,
};

interface TFormValues {
  phone: {
    phoneCode: string;
    phoneNumber: string;
  };
}

const ModalRegisterAnonymously: React.FC = () => {
  const s = useStyles();
  const {isOpen, close: onClose} = useModalById(MODAL_REGISTER_ANONYMOUSLY);

  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const [phoneTextHelper, setPhoneTextHelper] = useState(defaultPhoneTextHelper);

  const [phoneExistError, setPhoneExistError] = useState(false);

  const formInitialValues = {
    [phoneKey]: {
      [phoneCodeKey]: defaultPhoneCode,
    },
  };

  const onSubmit = async (values: TFormValues, form: FormApi) => {
    const phone = concatPhone(values.phone);
    try {
      logEvent({
        category: 'anonymous-modal',
        action: 'register-click',
      });
      const signUpData = await AuthService.easySignUp({phone});

      dispatch(modalHideAction(MODAL_REGISTER_ANONYMOUSLY));
      dispatch(modalShowAction(MODAL_VERIFICATION_CODE, {phone, signUpData}));
      setTimeout(form.reset);
    } catch (error) {
      if (/already exists and is verified/.test(error.message)) {
        setPhoneTextHelper({
          text: error.message,
          phone,
        });
        setPhoneExistError(true);
        return;
      }

      if (error && error.message) {
        errorSnackbar(
          snackbar.enqueueSnackbar,
          error && error.message ? error.message : `Something went wrong`
        );
      }
    }
  };

  const handlePhoneFieldChange = () => {
    setPhoneTextHelper(defaultPhoneTextHelper);
    setPhoneExistError(false);
  };

  const handleValidate = (values: TFormValues) => {
    return validateForm(values, [phoneKey], false);
  };

  useEffect(() => {
    if (isOpen) {
      logEvent({
        category: 'anonymous-modal',
        action: 'show-modal',
      });
    }
  }, [isOpen]);

  return (
    <ModalBase showCloseButton open={isOpen} onClose={onClose}>
      <div className={clsx('test-modal', s.wrapper)}>
        <ModalBaseContent
          title="Register Anonymously"
          description={
            <>
              <p>Register anonymously using only your phone!</p>
              <p>
                Enter your phone number and we will send you a verification code and your password
                to the free texting world!
              </p>
            </>
          }
        />

        <JSFinalFormReduxForm
          onSubmit={onSubmit}
          validate={handleValidate}
          keepDirtyOnReinitialize
          initialValues={formInitialValues}
          name={easySignUpForm}
          render={(prop: {handleSubmit: () => void}) => (
            <form onSubmit={prop.handleSubmit} className="test-signup-form">
              <Box mb={3}>
                <Field
                  name={phoneKey}
                  onChangeWatcher={handlePhoneFieldChange}
                  component={JSFinalFormPhoneInput}
                  helperText={
                    phoneTextHelper && (
                      <FormHelperText margin="dense" variant="outlined">
                        <span className="text-primary">{phoneTextHelper.text}</span>
                        {phoneExistError && phoneTextHelper.phone && (
                          <Link
                            className={s.proceedLink}
                            href={`${routeByName.signIn}?phone=${encodeURIComponent(
                              phoneTextHelper.phone
                            )}`}
                          >
                            Please proceed to login form
                          </Link>
                        )}
                      </FormHelperText>
                    )
                  }
                />
              </Box>
              <FormSpy>
                {(formProps: FormState<FormSpyRenderProps>) => {
                  if (!(phoneExistError && formProps.invalid)) {
                    return null;
                  }

                  return (
                    <Box mb={3} textAlign="center">
                      <Link
                        color="inherit"
                        underline="always"
                        href={routeByName.signIn}
                        component="a"
                      >
                        Please proceed to login form
                      </Link>
                    </Box>
                  );
                }}
              </FormSpy>
              <FormSpy>
                {(finalFormButtonProps: FormState<FormSpyRenderProps>) => (
                  <FinalFormButton {...finalFormButtonProps} type="submit">
                    Send me the code
                  </FinalFormButton>
                )}
              </FormSpy>
            </form>
          )}
        />
      </div>
    </ModalBase>
  );
};

export default ModalRegisterAnonymously;
